// SheetShed Theme - Warm, friendly, and approachable
// With dark mode support

const lightColors = {
  // Backgrounds - Warm cream tones
  background: '#FBF8F4',
  backgroundAlt: '#F5F0E8',
  backgroundGradientStart: '#E8F4F0',
  backgroundGradientEnd: '#FBF8F4',
  
  // Surfaces
  surface: '#FFFFFF',
  surfaceElevated: '#FDFCFA',
  surfaceHover: '#F8F5F0',
  surfaceDark: '#1A1A1C',
  surfaceFrosted: 'rgba(255, 255, 255, 0.85)',
  
  // Primary accent - Warm rose/coral
  primary: '#C75B7A',
  primaryLight: '#E8A0B4',
  primaryMuted: '#D4849A',
  primaryDark: '#9A4259',
  
  // Secondary - Deep burgundy
  secondary: '#6B2D3C',
  secondaryLight: '#8B4D5C',
  
  // Accent
  accent: '#F5E6C8',
  accentDark: '#E8D4A8',
  
  // Text colors
  textPrimary: '#1A1A1C',
  textSecondary: '#5A5A5E',
  textMuted: '#8A8A8E',
  textOnPrimary: '#FFFFFF',
  textOnDark: '#FFFFFF',
  textOnDarkMuted: '#B0B0B5',
  
  // Feedback
  success: '#4CAF7A',
  warning: '#E8A84C',
  error: '#D4544C',
  
  // Borders
  border: '#E8E4DC',
  borderLight: '#F0EDE8',
  borderDark: '#2D2D30',
  
  // Overlays
  overlay: 'rgba(26, 26, 28, 0.4)',
  overlayLight: 'rgba(255, 255, 255, 0.8)',
  
  // Cursor follower
  cursorGlow: 'rgba(199, 91, 122, 0.15)',
};

const darkColors = {
  // Backgrounds - Deep charcoal
  background: '#0D0D0F',
  backgroundAlt: '#141416',
  backgroundGradientStart: '#0D0D0F',
  backgroundGradientEnd: '#1A1A1E',
  
  // Surfaces
  surface: '#1A1A1E',
  surfaceElevated: '#222226',
  surfaceHover: '#2A2A2E',
  surfaceDark: '#0A0A0C',
  surfaceFrosted: 'rgba(26, 26, 30, 0.9)',
  
  // Primary accent - Warm rose/coral (brighter for dark mode)
  primary: '#E07A98',
  primaryLight: '#F0A0B8',
  primaryMuted: '#C8708A',
  primaryDark: '#B05070',
  
  // Secondary - Lighter burgundy
  secondary: '#9A4D5C',
  secondaryLight: '#B06070',
  
  // Accent
  accent: '#2A2520',
  accentDark: '#1E1A16',
  
  // Text colors
  textPrimary: '#F5F5F5',
  textSecondary: '#A0A0A5',
  textMuted: '#6A6A6E',
  textOnPrimary: '#0D0D0F',
  textOnDark: '#F5F5F5',
  textOnDarkMuted: '#808085',
  
  // Feedback
  success: '#5ABF8A',
  warning: '#F0B85C',
  error: '#E0645C',
  
  // Borders
  border: '#2A2A2E',
  borderLight: '#222226',
  borderDark: '#3A3A3E',
  
  // Overlays
  overlay: 'rgba(0, 0, 0, 0.6)',
  overlayLight: 'rgba(26, 26, 30, 0.9)',
  
  // Cursor follower
  cursorGlow: 'rgba(224, 122, 152, 0.2)',
};

export const theme = {
  light: lightColors,
  dark: darkColors,
  
  // Default to light
  colors: lightColors,
  
  spacing: {
    xs: 4,
    sm: 8,
    md: 16,
    lg: 24,
    xl: 32,
    xxl: 48,
    xxxl: 64,
    huge: 96,
  },
  
  borderRadius: {
    sm: 8,
    md: 12,
    lg: 16,
    xl: 24,
    xxl: 32,
    xxxl: 48,
    pill: 9999,
  },
  
  typography: {
    fontFamily: {
      display: '"Outfit", "DM Sans", -apple-system, BlinkMacSystemFont, sans-serif',
      body: '"Inter", "DM Sans", -apple-system, BlinkMacSystemFont, sans-serif',
      serif: '"Playfair Display", Georgia, serif',
    },
    
    fontSize: {
      xs: 12,
      sm: 14,
      md: 16,
      lg: 18,
      xl: 22,
      xxl: 28,
      xxxl: 40,
      display: 56,
      hero: 72,
      massive: 96,
    },
    
    fontWeight: {
      regular: '400',
      medium: '500',
      semibold: '600',
      bold: '700',
      black: '800',
    },
    
    lineHeight: {
      tight: 1.1,
      normal: 1.4,
      relaxed: 1.6,
    },
  },
  
  shadows: {
    sm: '0 2px 8px rgba(0, 0, 0, 0.04)',
    md: '0 4px 16px rgba(0, 0, 0, 0.06)',
    lg: '0 8px 32px rgba(0, 0, 0, 0.08)',
    xl: '0 16px 48px rgba(0, 0, 0, 0.1)',
    float: '0 8px 30px rgba(0, 0, 0, 0.12)',
    navbar: '0 4px 24px rgba(0, 0, 0, 0.08)',
    card: '0 2px 12px rgba(0, 0, 0, 0.05)',
    glow: '0 0 40px rgba(199, 91, 122, 0.2)',
  },
  
  transitions: {
    fast: '150ms ease-out',
    normal: '250ms ease-out',
    slow: '400ms ease-out',
    bounce: '500ms cubic-bezier(0.34, 1.56, 0.64, 1)',
  },
};

export default theme;
